import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { Calendar, Clock, MapPin, Phone, Mail, Globe, BookOpen, Users, Calendar as CalendarIcon } from 'lucide-react';

const SchoolProfileForTeacher = () => {
  const { api, user } = useAuth();
  const [schoolData, setSchoolData] = useState(null);
  const [classesData, setClassesData] = useState([]);
  const [sessionsData, setSessionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('classes');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch school details
        const schoolResponse = await api.get(`/api/v1/admin/schools/${user.school_reg}`);
        setSchoolData(schoolResponse.data);

        // Fetch classes
        const classesResponse = await api.get(`/api/v1/admin/schools/${user.school_reg}/classes`);
        setClassesData(classesResponse.data);

        // Fetch sessions
        const sessionsResponse = await api.get(`/api/v1/admin/schools/${user.school_reg}/sessions`);
        setSessionsData(sessionsResponse.data.items);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [api, user.school_reg]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  // Format weekdays for display
  const formatWeekdays = (weekdays) => {
    if (!weekdays || weekdays.length === 0) return 'None';
    
    // If all days are present
    if (weekdays.length === 7) return 'Every day';
    
    // Short version of days
    return weekdays.map(day => day.substring(0, 3)).join(', ');
  };

  // Format status for display with appropriate color
  const getStatusBadge = (status) => {
    const statusColors = {
      'active': 'bg-green-100 text-green-800',
      'inactive': 'bg-gray-100 text-gray-800',
      'upcoming': 'bg-blue-100 text-blue-800',
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Format time
  const formatTime = (time) => {
    if (!time) return '';
    
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const minute = minutes;
    
    const period = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
    
    return `${formattedHour}:${minute} ${period}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* School Profile Card */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-teal-700 to-teal-600 p-6 text-white">
          <h1 className="text-2xl md:text-3xl font-bold">{schoolData?.name || 'School Name'}</h1>
          <p className="mt-2 italic">"{schoolData?.extra_info?.motto || 'Education for a brighter future'}"</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="flex items-start space-x-3">
              <MapPin className="text-teal-700 mt-1 flex-shrink-0" size={18} />
              <div>
                <h4 className="font-medium text-gray-700">Address</h4>
                <p className="text-gray-600">{schoolData?.address}, {schoolData?.county}</p>
                <p className="text-gray-600">Postal Code: {schoolData?.postal_code}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Calendar className="text-teal-700 mt-1 flex-shrink-0" size={18} />
              <div>
                <h4 className="font-medium text-gray-700">Established</h4>
                <p className="text-gray-600">{schoolData?.extra_info?.established || 'N/A'}</p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start space-x-3">
              <Phone className="text-teal-700 mt-1 flex-shrink-0" size={18} />
              <div>
                <h4 className="font-medium text-gray-700">Phone</h4>
                <p className="text-gray-600">{schoolData?.phone || 'N/A'}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Mail className="text-teal-700 mt-1 flex-shrink-0" size={18} />
              <div>
                <h4 className="font-medium text-gray-700">Email</h4>
                <p className="text-gray-600">{schoolData?.email || 'N/A'}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Globe className="text-teal-700 mt-1 flex-shrink-0" size={18} />
              <div>
                <h4 className="font-medium text-gray-700">Website</h4>
                <p className="text-gray-600">{schoolData?.website || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs Navigation */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              className={`py-4 px-6 font-medium text-sm border-b-2 ${
                activeTab === 'classes'
                  ? 'border-teal-700 text-teal-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('classes')}
            >
              <div className="flex items-center">
                <BookOpen size={16} className="mr-2" />
                Classes & Streams
              </div>
            </button>
            <button
              className={`py-4 px-6 font-medium text-sm border-b-2 ${
                activeTab === 'sessions'
                  ? 'border-teal-700 text-teal-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('sessions')}
            >
              <div className="flex items-center">
                <Clock size={16} className="mr-2" />
                School Sessions
              </div>
            </button>
          </nav>
        </div>
        
        {/* Content for Classes & Streams */}
        {activeTab === 'classes' && (
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Classes & Streams</h2>
            
            {classesData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No classes found
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {classesData.map((classItem) => (
                  <div key={classItem.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 transition-all hover:shadow-md">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center mr-3">
                        <BookOpen size={20} className="text-teal-700" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">{classItem.name}</h3>
                    </div>
                    
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <Users size={16} className="mr-2 text-teal-700" />
                        Streams ({classItem.streams.length})
                      </h4>
                      
                      <div className="space-y-2">
                        {classItem.streams.map((stream) => (
                          <div key={stream.id} className="flex items-center p-2 bg-white rounded border border-gray-200">
                            <div className="w-6 h-6 rounded-full bg-teal-50 flex items-center justify-center mr-2">
                              <span className="text-xs font-medium text-teal-700">{stream.name.split(' ').pop()}</span>
                            </div>
                            <span className="text-gray-700">{stream.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Content for School Sessions */}
        {activeTab === 'sessions' && (
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">School Sessions</h2>
            
            {sessionsData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No sessions found
              </div>
            ) : (
              <div className="space-y-4">
                {sessionsData.map((session) => (
                  <div key={session.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    <div className="bg-gray-50 p-4 border-b border-gray-200 flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center mr-3">
                          <CalendarIcon size={20} className="text-teal-700" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">{session.name} Session</h3>
                          <p className="text-sm text-gray-600">{formatWeekdays(session.weekdays)}</p>
                        </div>
                      </div>
                      <div>
                        {getStatusBadge(session.status)}
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start space-x-3">
                          <Calendar className="text-teal-700 mt-1 flex-shrink-0" size={18} />
                          <div>
                            <h4 className="font-medium text-gray-700">Duration</h4>
                            <p className="text-gray-600">
                              {new Date(session.start_date).toLocaleDateString()} - {new Date(session.end_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                          <Clock className="text-teal-700 mt-1 flex-shrink-0" size={18} />
                          <div>
                            <h4 className="font-medium text-gray-700">Time</h4>
                            <p className="text-gray-600">
                              {formatTime(session.start_time)} - {formatTime(session.end_time)}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {session.description && (
                        <div className="mt-4 text-gray-600">
                          <h4 className="font-medium text-gray-700">Description</h4>
                          <p>{session.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SchoolProfileForTeacher;